/**
 * Cross-field and cross-record invariants.
 *
 * These hold between a rule and the rest of the document, so they cannot be expressed in a
 * per-object schema and run as a second pass over an already schema-valid rule set.
 *
 * All of them hold at 51/51 in the rule set as it stands. They are enforced rather than
 * assumed because each one, if broken, produces a rule that is well-formed but wrong — and a
 * rule that is wrong in these particular ways produces a wrong state in a report.
 *
 * Every check here is keyed on category, check type or declared data. None is keyed on a rule
 * ID; a branch on a specific rule ID would mean the rule set had stopped being data.
 */

import type { Category, Rule, Ruleset } from './schema.js';
import type { RulesetDefect } from './errors.js';
import { WEIGHTED_TIERS, tierCarriesWeight } from './vocabulary.js';

/** Check types that may never auto-fail, whatever the rule says. */
const ALWAYS_REVIEW_ONLY = {
  /**
   * Hard constraint 4. Dosing detection matches a mass unit near a schedule word, which is a
   * suggestive coincidence rather than an observation. "5mg vial, ships daily" trips it.
   * These go to a human queue regardless of confidence.
   */
  text_cooccurrence: 'co-occurrence matching is suggestive rather than conclusive',
  /**
   * A manual rule is never executed at all — it always returns `not_evaluable` and documents
   * the gap. It has nothing to auto-fail on.
   */
  manual: 'a manual check never runs, so it can never observe a violation',
} as const satisfies Partial<Record<Rule['type'], string>>;

function defect(ruleId: string, path: string, message: string): RulesetDefect {
  return { ruleId, path, message };
}

/**
 * Uniqueness across the `categories` block.
 *
 * `prefix` uniqueness is what makes prefix-matches-category decidable: two categories sharing
 * a prefix would leave a rule ID belonging to both.
 */
/**
 * Direction, where a rule set uses it (D-290).
 *
 * Three properties, and each one is a way the report could otherwise mislead:
 *
 *   - **A rule that cites a source declares which way it runs.** A set that uses directions and left
 *     one off would drop that finding out of every group built on the relationship, silently.
 *   - **A rule Mintro wrote declares none.** Mintro quotes nobody, so there is no source's text to
 *     read a direction off, and a direction there would be Mintro's own view wearing a citation's
 *     clothes. Held whether or not the set uses directions elsewhere.
 *   - **It agrees with the sense.** `requires` goes with a thing that must be present, `prohibits`
 *     with one that must be absent. They are the same fact said twice, and two facts that can
 *     disagree eventually do (D-181) — the report reads `sense` for the label and `direction` for the
 *     group, so a mismatch would file "Observed" under the heading for what was not found.
 */
function checkDirection(rules: readonly IndexedRule[]): RulesetDefect[] {
  const defects: RulesetDefect[] = [];
  const used = rules.some(({ rule }) => rule.direction !== undefined);

  for (const { rule, index } of rules) {
    if (rule.source === 'mintro') {
      if (rule.direction !== undefined) {
        defects.push(
          defect(
            rule.id,
            `rules[${index}].direction`,
            "a rule Mintro wrote cites no source, so there is no source's text to take a direction from",
          ),
        );
      }
      continue;
    }

    if (used && rule.direction === undefined) {
      defects.push(
        defect(
          rule.id,
          `rules[${index}].direction`,
          'this rule set declares directions, so every rule citing a source must declare which way it runs',
        ),
      );
      continue;
    }

    if (rule.direction === undefined) continue;

    const wanted = rule.direction === 'requires' ? 'present' : 'absent';
    const sense = rule.sense ?? ('expect' in rule.params ? rule.params.expect : undefined);
    if (sense !== undefined && sense !== wanted) {
      defects.push(
        defect(
          rule.id,
          `rules[${index}].direction`,
          `direction '${rule.direction}' and sense '${String(sense)}' disagree: ` +
            `'${rule.direction}' goes with sense '${wanted}'`,
        ),
      );
    }
  }
  return defects;
}

function checkCategoryUniqueness(categories: readonly Category[]): RulesetDefect[] {
  const defects: RulesetDefect[] = [];
  const fields = [
    { key: 'id', label: 'category id' },
    { key: 'prefix', label: 'category prefix' },
    { key: 'n', label: 'category number' },
  ] as const;

  for (const { key, label } of fields) {
    const firstSeenAt = new Map<string, number>();
    categories.forEach((category, index) => {
      const value = String(category[key]);
      const previous = firstSeenAt.get(value);
      if (previous === undefined) {
        firstSeenAt.set(value, index);
        return;
      }
      defects.push({
        path: `categories[${index}].${key}`,
        message: `duplicate ${label} '${value}' — already declared at categories[${previous}]`,
      });
    });
  }
  return defects;
}

/**
 * Rule IDs are stable and never reused (CLAUDE.md § Conventions), which makes a duplicate a
 * silent overwrite: two findings claiming the same identity in the same report.
 */
function checkRuleIdUniqueness(rules: readonly IndexedRule[]): RulesetDefect[] {
  const defects: RulesetDefect[] = [];
  const firstSeenAt = new Map<string, number>();

  for (const { rule, index } of rules) {
    const previous = firstSeenAt.get(rule.id);
    if (previous === undefined) {
      firstSeenAt.set(rule.id, index);
      continue;
    }
    defects.push(
      defect(
        rule.id,
        `rules[${index}].id`,
        `duplicate rule id — already used at rules[${previous}]. Rule IDs are stable and never reused.`,
      ),
    );
  }
  return defects;
}

/**
 * A rule paired with its position in the original document, so defects can be reported
 * against the right array index even when the rules being checked are a subset.
 */
export interface IndexedRule {
  readonly rule: Rule;
  readonly index: number;
}

/** Per-rule invariants that depend on the document around the rule. */
function checkRule(
  rule: Rule,
  index: number,
  categoriesById: Map<string, Category>,
  knownRuleIds: ReadonlySet<string>,
  allRules: ReadonlyMap<string, Rule>,
): RulesetDefect[] {
  const defects: RulesetDefect[] = [];
  const at = (field: string): string => `rules[${index}].${field}`;

  // The category must exist. An unknown category leaves the finding unfiled in the report.
  const category = categoriesById.get(rule.cat);
  if (category === undefined) {
    const known = [...categoriesById.keys()].join(', ');
    defects.push(
      defect(rule.id, at('cat'), `unknown category '${rule.cat}' — declared categories are: ${known}`),
    );
  } else {
    // The ID prefix must match the category's declared prefix (D-008). Read from
    // `categories[].prefix`, never from a table in this file.
    const prefix = rule.id.slice(0, rule.id.indexOf('-'));
    if (prefix !== category.prefix) {
      defects.push(
        defect(
          rule.id,
          at('id'),
          `prefix '${prefix}' does not match category '${category.id}', which declares prefix '${category.prefix}'`,
        ),
      );
    }
  }

  // `layer: null` and `type: "manual"` mean the same thing — the rule is not reachable by
  // crawling — and must agree. A manual rule with a layer would be queued for a crawl that
  // cannot evaluate it; a non-manual rule without one gives the runner no point to run it.
  if (rule.type === 'manual' && rule.layer !== null) {
    defects.push(
      defect(
        rule.id,
        at('layer'),
        `manual rules are not reachable by crawling and must have layer null, found ${rule.layer}`,
      ),
    );
  }
  if (rule.type !== 'manual' && rule.layer === null) {
    defects.push(
      defect(
        rule.id,
        at('layer'),
        `layer null is reserved for manual rules; a ${rule.type} rule needs a crawl layer (0-3)`,
      ),
    );
  }

  // `weight` belongs to the weighted tiers and to nothing else (D-259, amended 2026-09-09).
  //
  // Both directions are defects, and neither is cosmetic. A weighted-tier rule with no weight is a
  // rule an angle cannot weigh, so it would be cited as though it were ordinary and nobody would
  // be told the difference — the heavy set is exactly the rules that must not be flattened. A
  // legality rule carrying a weight is the opposite error: it implies the tier is weighed, when a
  // legality item observed ends the evaluation outright and there is nothing for a number to
  // modulate. A number nothing reads is worse than no number, because a later reader assumes
  // something reads it.
  //
  // Routing moved from the second case to the first in the amendment. The conditions are not
  // equally consequential and flattening them lost the difference between a catalogue selling
  // syringes and an untidy affiliate page.
  if (tierCarriesWeight(rule.evaluation_tier) && rule.weight === undefined) {
    defects.push(
      defect(
        rule.id,
        at('weight'),
        `a ${rule.evaluation_tier} rule must declare a weight (heavy | ordinary); an unweighted one would be cited as ordinary with nothing saying so`,
      ),
    );
  }
  if (!tierCarriesWeight(rule.evaluation_tier) && rule.weight !== undefined) {
    defects.push(
      defect(
        rule.id,
        at('weight'),
        `weight is for the ${WEIGHTED_TIERS.join(' and ')} tiers only; a ${rule.evaluation_tier} rule is not weighed, found '${rule.weight}'`,
      ),
    );
  }

  // A collecting rule gathers an observation and settles nothing, so its finding is always
  // `review` (D-133). `stateForViolation` would turn that into `fail` on an auto_fail rule, which
  // would auto-fail every merchant who links a social account. The tier is the guard, so it is
  // required here rather than trusted.
  if (rule.type === 'dom_assert' && rule.params.collect !== undefined && rule.tier !== 'review_only') {
    defects.push(
      defect(
        rule.id,
        at('tier'),
        `a rule that collects '${rule.params.collect}' settles nothing and must be review_only, found ${rule.tier}`,
      ),
    );
  }

  // A rule referenced by another must exist. A dangling `target_phrases_from` would leave a
  // critical rule with no subject, which would silently disable it (D-015). Checked for every
  // check type that can declare a subject, not just the first one that needed to.
  if (rule.type === 'computed_style' || rule.type === 'dom_assert') {
    const target = rule.params.target_phrases_from;
    if (target !== undefined && !knownRuleIds.has(target)) {
      defects.push(
        defect(
          rule.id,
          at('params.target_phrases_from'),
          `references rule '${target}', which is not in the rule set — this rule would have no subject to measure`,
        ),
      );
    }
    if (target === rule.id) {
      defects.push(
        defect(rule.id, at('params.target_phrases_from'), 'references itself'),
      );
    }
  }

  /*
    A blocking flag must say who ruled it (D-161).

    `blocking` is the highest-consequence field a rule carries: it puts the rule in front of an
    operator as a stopping condition. Unattributed, it is an authority nobody stated — the exact
    failure `source` exists to prevent, one field over. The pairing is checked here rather than in
    the schema because a Zod refinement would report it as a shape error rather than as the rule
    defect it is.
  */
  if (rule.blocking === true && rule.blocking_source === undefined) {
    defects.push(
      defect(rule.id, at('blocking'), 'is marked blocking but names no authority in blocking_source'),
    );
  }
  if (rule.blocking !== true && rule.blocking_source !== undefined) {
    defects.push(
      defect(rule.id, at('blocking_source'), 'names a blocking authority but is not marked blocking'),
    );
  }

  /*
    A corroboration pair must exist, be mutual, and not be a rule with itself (D-050).

    Mutual because the report renders the pair on **both** findings. A one-sided declaration
    would show the relation on one finding and not the other, so a reader arriving at the second
    would not know the first existed — and which finding a reader opens first is not something
    the rule set gets to decide.
  */
  for (const partner of rule.corroborates ?? []) {
    if (partner === rule.id) {
      defects.push(defect(rule.id, at('corroborates'), 'names itself'));
      continue;
    }
    if (!knownRuleIds.has(partner)) {
      defects.push(
        defect(
          rule.id,
          at('corroborates'),
          `names rule '${partner}', which is not in the rule set`,
        ),
      );
      continue;
    }
    const other = allRules.get(partner);
    if (other !== undefined && !(other.corroborates ?? []).includes(rule.id)) {
      defects.push(
        defect(
          rule.id,
          at('corroborates'),
          `names '${partner}', but '${partner}' does not name it back — the report renders the ` +
            `pair on both findings, so the relation has to be declared on both rules`,
        ),
      );
    }
  }

  // Tiers that the check type forbids. Hard constraint 4.
  const forbidden = ALWAYS_REVIEW_ONLY[rule.type as keyof typeof ALWAYS_REVIEW_ONLY];
  if (forbidden !== undefined && rule.tier !== 'review_only') {
    defects.push(
      defect(
        rule.id,
        at('tier'),
        `${rule.type} rules must be tier 'review_only', found '${rule.tier}' — ${forbidden}`,
      ),
    );
  }

  return defects;
}

/**
 * Runs every invariant over a set of schema-valid categories and rules.
 *
 * Takes the rules as an indexed subset rather than a whole rule set, so that a document which
 * failed schema validation can still have its *remaining* rules checked. Without that, a rule
 * set with one malformed param and one broken invariant reports only the param, and the
 * second problem surfaces on the next run — which is the fix-one-at-a-time cycle the
 * aggregate error exists to avoid.
 *
 * Returns an empty array when everything checked is sound. Never throws — the caller
 * aggregates these with schema defects and raises them together.
 */
export function checkInvariantsOn(
  categories: readonly Category[],
  rules: readonly IndexedRule[],
): RulesetDefect[] {
  const defects: RulesetDefect[] = [
    ...checkCategoryUniqueness(categories),
    ...checkRuleIdUniqueness(rules),
    ...checkDirection(rules),
  ];

  const categoriesById = new Map(categories.map((category) => [category.id, category]));
  const knownRuleIds = new Set(rules.map(({ rule }) => rule.id));
  const allRules = new Map(rules.map(({ rule }) => [rule.id, rule]));
  for (const { rule, index } of rules) {
    defects.push(...checkRule(rule, index, categoriesById, knownRuleIds, allRules));
  }

  return defects;
}

/** Runs every invariant over a complete, schema-valid rule set. */
export function checkInvariants(ruleset: Pick<Ruleset, 'categories' | 'rules'>): RulesetDefect[] {
  return checkInvariantsOn(
    ruleset.categories,
    ruleset.rules.map((rule, index) => ({ rule, index })),
  );
}
